import { joinSurveyAction } from '../join-survey';
import type { ConnectChannel } from '../types';

/**
 * Static content for the Digital Ads channel detail page
 * (`/connect/digital-ads`). Same sourcing rule as demographics.ts: copied,
 * not imported, since the source (Data Catalog / EXPLORE_CHANNELS) backs
 * internal-only tooling and this page is visible to every Home user.
 */
export const DIGITAL_ADS_CHANNEL: ConnectChannel = {
    name: 'Digital Ads',
    icon: 'megaphone',
    color: '#639D84',
    subtitle: 'US · Social, video, audio & AI platforms',
    subtitleLine2: 'Daily · Since 2024',
    exploreDaniPrompt: 'What can digital ad exposure data tell me about my consumers?',
    properties: [
        { name: 'YouTube', letter: 'YT', color: '#FF0000', icon: 'logos:youtube-icon' },
        { name: 'Facebook', letter: 'f', color: '#1877F2', icon: 'logos:facebook' },
        { name: 'Instagram', letter: 'IG', color: '#E1306C', icon: 'logos:instagram-icon' },
        { name: 'X', letter: 'X', color: '#000000', icon: 'logos:x' },
        { name: 'TikTok', letter: 'TT', color: '#000000', icon: 'logos:tiktok-icon' },
        {
            name: 'Pandora',
            letter: 'Pa',
            color: '#3668FF',
            icon: 'simple-icons:pandora',
            iconColor: '#3668FF',
        },
        { name: 'Spotify', letter: 'Sp', color: '#1DB954', icon: 'logos:spotify-icon' },
        { name: 'Soundcloud', letter: 'SC', color: '#FF5500', icon: 'logos:soundcloud' },
        {
            name: 'YouTube Music',
            letter: 'YM',
            color: '#FF0000',
            icon: 'simple-icons:youtubemusic',
            iconColor: '#FF0000',
        },
        { name: 'ChatGPT', letter: 'AI', color: '#418363', icon: 'logos:openai-icon' },
    ],
    actions: [
        {
            id: 'ads-exposure',
            name: 'Measure exposure',
            icon: 'megaphone',
            summary: 'who saw your social ads, on which platforms and for how long',
            template:
                'Who was exposed to social ads from {brand|my brand} over {window|the last 12 weeks}, by platform and duration?',
        },
        {
            id: 'ads-creatives',
            name: 'Compare creatives',
            icon: 'eye',
            summary: 'which of your ad creatives perform best',
            template:
                'Compare performance across ad creatives from {brand|my brand} over {window|the last 12 weeks}',
        },
        {
            id: 'ads-attention',
            name: 'Measure attention',
            icon: 'pie-chart',
            summary: "how much attention your ads get next to competitors'",
            template:
                'How much attention do ads from {brand|my brand} get compared to {competitors|competitors} over {window|the last 12 weeks}?',
        },
        joinSurveyAction('digital-ads'),
    ],
    schema: [
        { field: 'platform', type: 'string', description: 'Platform the ad was shown on' },
        {
            field: 'surface',
            type: 'string',
            description: 'Where within the platform the ad appeared',
        },
        {
            field: 'advertiser_name',
            type: 'string',
            description: 'Advertiser responsible for the ad',
        },
        {
            field: 'ad_action',
            type: 'string',
            description: 'Impression / viewed / clicked / played',
        },
        { field: 'source_feed', type: 'string', description: 'Ad source this exposure came from' },
        { field: 'ad_event_date_utc', type: 'date', description: 'Date the ad was shown (UTC)' },
    ],
    sampleCols: ['Platform', 'Surface', 'Advertiser', 'Action', 'Date'],
    sampleColWidths: [
        'minmax(90px, 1fr)',
        'minmax(120px, 1.5fr)',
        'minmax(90px, 1fr)',
        '90px',
        '90px',
    ],
    sampleRows: [
        ['ChatGPT', 'In-conversation card', 'HelloFresh', 'impression', '2026-08-14'],
        ['Instagram', 'Feed post', 'Nike', 'viewed', '2026-08-14'],
        ['TikTok', 'For You', 'Chipotle', 'impression', '2026-08-13'],
        ['YouTube', 'Pre-roll', '—', 'played', '2026-08-13'],
        ['Spotify', 'Audio break', 'State Farm', 'played', '2026-08-12'],
    ],
};
